import { useState, useEffect, useCallback } from 'react';
import { KanbanCard, KanbanColumn } from '@/types/kanban';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { setupRealtimeKanban, checkForApproachingDeadlines } from '@/utils/supabase/realtimeUtils';
import { createNotification } from '@/utils/notificationUtils';
import { useQueryClient } from '@tanstack/react-query';

interface NewTask {
  title: string;
  description: string;
  priority: string;
  dueDate: string;
  assignees?: any[];
  departmentId?: string;
}

function toSlug(str) {
  return str?.toLowerCase().replace(/\s+/g, '-').trim();
}

export function useKanbanBoard() {
  const [columns, setColumns] = useState<KanbanColumn[]>([]);
  const [cards, setCards] = useState<KanbanCard[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMutating, setIsMutating] = useState(false);
  const [newTaskColumn, setNewTaskColumn] = useState<string | null>(null);
  const [activeCard, setActiveCard] = useState<KanbanCard | null>(null);
  const [newTask, setNewTask] = useState<NewTask>({
    title: '',
    description: '',
    priority: 'medium',
    dueDate: new Date().toISOString().split('T')[0],
  });
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const fetchColumns = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('kanban_columns')
        .select('*')
        .order('order_index');

      if (error) throw error;
      
      if (data) {
        const formattedColumns: KanbanColumn[] = data.map(col => ({
          ...col,
          cards: [], // Initialize with empty cards array
          iconName: col.icon_name,
          iconColor: col.icon_color
        }));
        setColumns(formattedColumns);
        return formattedColumns;
      }
      return [];
    } catch (error: any) {
      toast({
        title: 'Error loading columns',
        description: error.message,
        variant: 'destructive',
      });
      return [];
    }
  }, [toast]);

  const fetchCards = useCallback(async () => {
    try {
      // Get all cards with their assignees
      const { data: cardData, error: cardError } = await supabase
        .from('kanban_cards')
        .select(`
          *,
          kanban_card_assignees (
            employee_id,
            employees (id, name, avatar)
          )
        `)
        .order('created_at', { ascending: false });
      
      if (cardError) throw cardError;

      if (cardData) {
        // Format cards with proper assignees
        const formattedCards: KanbanCard[] = cardData.map(card => ({
          id: card.id,
          title: card.title,
          description: card.description || '',
          columnId: card.column_id,
          priority: card.priority,
          dueDate: card.due_date,
          progress: card.progress,
          departmentId: card.department_id,
          mfo_id: card.mfo_id,
          assignees: card.kanban_card_assignees.map((assignee: any) => ({
            id: assignee.employee_id,
            name: assignee.employees.name,
            avatar: assignee.employees.avatar
          }))
        }));

        setCards(formattedCards);
        return formattedCards;
      }
      return [];
    } catch (error: any) {
      toast({
        title: 'Error loading cards',
        description: error.message,
        variant: 'destructive',
      });
      return [];
    }
  }, [toast]);

  const loadKanbanData = useCallback(async () => {
    setIsLoading(true);
    try {
      const fetchedColumns = await fetchColumns();
      const fetchedCards = await fetchCards();
      
      // Check for approaching deadlines - moved to the App component
      const deadlines = await checkForApproachingDeadlines();
      deadlines.forEach(mfo => {
        const dueDate = new Date(mfo.due_date);
        const today = new Date();
        const daysUntilDue = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        
        createNotification(
          "Deadline Approaching",
          `MFO "${mfo.code}: ${mfo.title}" is due in ${daysUntilDue} day${daysUntilDue !== 1 ? 's' : ''}`,
          "deadline_approaching"
        );
      });
      
      // Set in query cache
      queryClient.setQueryData(['kanban'], { columns: fetchedColumns, cards: fetchedCards });
    } finally {
      setIsLoading(false);
    }
  }, [fetchColumns, fetchCards, queryClient]);

  const fetchAllData = useCallback(() => {
    loadKanbanData();
  }, [loadKanbanData]);
  
  useEffect(() => {
    // Try to get from query cache first
    const cached = queryClient.getQueryData(['kanban']);
    if (cached) {
      const { columns: cachedColumns, cards: cachedCards } = cached as { columns: KanbanColumn[], cards: KanbanCard[] };
      if (cachedColumns?.length > 0 && cachedCards?.length > 0) {
        setColumns(cachedColumns);
        setCards(cachedCards);
        setIsLoading(false);
      } else {
        loadKanbanData();
      }
    } else {
      loadKanbanData();
    }
    
    // Set up realtime updates for kanban
    const unsubscribe = setupRealtimeKanban(() => {
      // Only reload if we're not currently mutating (i.e., not moving a card)
      if (!isMutating) {
        loadKanbanData();
      }
    });
    
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [loadKanbanData, queryClient, isMutating]);
  
  // Group cards by their column
  const columnsWithCards = columns.map(column => {
    const columnCards = cards.filter(card => card.columnId === column.id);
    return {
      ...column,
      cards: columnCards
    };
  });

  const moveCard = async (cardId: string, targetColumnId: string) => {
    if (isMutating) return;
    
    // Find the card and the target column
    const cardToMove = cards.find(card => card.id === cardId);
    const targetColumn = columns.find(col => col.id === targetColumnId);
    
    if (!cardToMove || !targetColumn) {
      throw new Error("Card or column not found");
    }
    
    try {
      setIsMutating(true);
      
      // Update local state first for optimistic UI update
      setCards(prev => 
        prev.map(card => 
          card.id === cardId 
            ? { ...card, columnId: targetColumnId } 
            : card
        )
      );
      
      // Update the card in the database
      const { data, error } = await supabase
        .from('kanban_cards')
        .update({ column_id: targetColumnId })
        .eq('id', cardId)
        .select();
        
      if (error) throw error;
      
      if (data) {
        // If the card is linked to an MFO, update its status as well
        if (data[0]?.mfo_id) {
          // First, get the column name to map to MFO status
          const { data: columnData, error: columnError } = await supabase
            .from('kanban_columns')
            .select('title')
            .eq('id', targetColumnId)
            .single();
            
          if (!columnError && columnData) {
            // Set MFO status to slug of column title
            const mfoStatus = toSlug(columnData.title);
            // Update the MFO status
            const { error: mfoError } = await supabase
              .from('mfo_items')
              .update({ status: mfoStatus })
              .eq('id', data[0].mfo_id);
              
            if (mfoError) {
              console.error('Error updating MFO status:', mfoError);
            }
          }
        }
        
        // Create a notification about the card being moved
        createNotification(
          "Card Moved",
          `Task "${cardToMove.title}" has been moved to "${targetColumn.title}"`,
          "task_moved"
        );
        
        // Invalidate relevant queries without triggering loading state
        queryClient.invalidateQueries({ queryKey: ['kanban'] });
        queryClient.invalidateQueries({ queryKey: ['mfoItems'] });
        
        toast({
          title: 'Card moved',
          description: 'Task card has been moved successfully',
        });
      }
    } catch (error: any) {
      // Revert the optimistic update if there was an error
      setCards(prev => 
        prev.map(card => 
          card.id === cardId 
            ? { ...card, columnId: cardToMove.columnId } 
            : card
        )
      );
      
      toast({
        title: 'Error moving card',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsMutating(false);
    }
  };

  const deleteCard = async (cardId: string) => {
    if (isMutating) return;
    
    try {
      setIsMutating(true);
      
      // Check if card is linked to MFO
      const cardToDelete = cards.find(card => card.id === cardId);
      
      if (cardToDelete?.mfo_id) {
        toast({
          title: 'Cannot delete',
          description: 'This card is linked to an MFO item. Delete the MFO item instead.',
          variant: 'destructive',
        });
        return;
      }
      
      const { error } = await supabase
        .from('kanban_cards')
        .delete()
        .eq('id', cardId);
        
      if (error) throw error;
      
      // Update local state
      setCards(prev => prev.filter(card => card.id !== cardId));
      
      // Invalidate query cache
      queryClient.invalidateQueries({ queryKey: ['kanban'] });
      
      toast({
        title: 'Card deleted',
        description: 'Task card has been deleted successfully',
      });
    } catch (error: any) {
      toast({
        title: 'Error deleting card',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsMutating(false);
    }
  };

  // Adding the missing functions needed for KanbanBoard.tsx
  const handleDragStart = (cardId: string) => {
    const card = cards.find(c => c.id === cardId);
    if (card) {
      setActiveCard(card);
    }
  };

  const handleDragOver = (columnId: string) => {
    // Implementation for drag over handling
  };

  const handleDrop = (columnId: string) => {
    if (activeCard && activeCard.columnId !== columnId) {
      moveCard(activeCard.id, columnId);
    }
    setActiveCard(null);
  };

  const openAddTaskModal = (columnId: string) => {
    setNewTaskColumn(columnId);
    setIsModalOpen(true);
  };

  const handleAddTask = async (task: NewTask) => {
    if (!newTaskColumn || isMutating) return;
    
    try {
      setIsMutating(true);
      
      const { data, error } = await supabase
        .from('kanban_cards')
        .insert({
          title: task.title,
          description: task.description,
          column_id: newTaskColumn,
          priority: task.priority,
          due_date: task.dueDate,
          progress: 0,
          department_id: task.departmentId
        })
        .select();
        
      if (error) throw error;
      
      setIsModalOpen(false);
      
      // Reset the form
      setNewTask({
        title: '',
        description: '',
        priority: 'medium',
        dueDate: new Date().toISOString().split('T')[0],
      });
      
      // Refresh data
      fetchAllData();
      
      // Invalidate query cache
      queryClient.invalidateQueries({ queryKey: ['kanban'] });
      
      // Create notification
      createNotification(
        "Task Created",
        `New task "${task.title}" has been created`,
        "task_created"
      );
      
      toast({
        title: 'Task added',
        description: 'Task has been added successfully',
      });
    } catch (error: any) {
      toast({
        title: 'Error adding task',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsMutating(false);
    }
  };

  const handleDeleteTask = (taskId: string) => {
    deleteCard(taskId);
  };

  const handleTransferTask = (taskId: string, targetColumnId: string) => {
    moveCard(taskId, targetColumnId);
  };

  const createColumn = async (title: string, icon_name: string, icon_color: string) => {
    if (isMutating) return;
    
    try {
      setIsMutating(true);
      
      const newOrder = columns.length;

      const { data, error } = await supabase
        .from('kanban_columns')
        .insert({
          title,
          icon_name: icon_name || 'Circle',
          icon_color: icon_color || '#64748b',
          order_index: newOrder
        })
        .select();
        
      if (error) throw error;
      
      // Invalidate query cache
      queryClient.invalidateQueries({ queryKey: ['kanban'] });
      
      fetchAllData();
      
      toast({
        title: 'Column added',
        description: 'Column has been added successfully',
      });
    } catch (error: any) {
      toast({
        title: 'Error adding column',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsMutating(false);
    }
  };

  const reorderColumn = async (oldIndex: number, newIndex: number) => {
    if (isMutating) return;
    
    try {
      setIsMutating(true);
      
      // Make a copy of columns before reordering to revert if needed
      const originalColumns = [...columns];
      
      // Update all affected columns
      const updates = columns.map((col, index) => {
        let newOrderIndex = col.order_index;
        
        if (index === oldIndex) {
          newOrderIndex = newIndex;
        } else if (oldIndex < newIndex) {
          if (index > oldIndex && index <= newIndex) {
            newOrderIndex = col.order_index - 1;
          }
        } else if (oldIndex > newIndex) {
          if (index >= newIndex && index < oldIndex) {
            newOrderIndex = col.order_index + 1;
          }
        }
        
        return {
          id: col.id,
          order_index: newOrderIndex
        };
      });
      
      // Update in database
      for (const update of updates) {
        const { error } = await supabase
          .from('kanban_columns')
          .update({ order_index: update.order_index })
          .eq('id', update.id);
          
        if (error) {
          // If error, revert the column ordering
          setColumns(originalColumns);
          throw error;
        }
      }
      
      // Invalidate query cache
      queryClient.invalidateQueries({ queryKey: ['kanban'] });
      
      // Refresh data after all updates
      fetchAllData();
      
      toast({
        title: 'Columns reordered',
        description: 'Column order has been updated',
      });
    } catch (error: any) {
      toast({
        title: 'Error reordering columns',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsMutating(false);
    }
  };

  return {
    columns: columnsWithCards,
    cards,
    isLoading,
    moveCard,
    deleteCard,
    refreshData: fetchAllData,
    setColumns,
    isModalOpen,
    setIsModalOpen,
    newTaskColumn,
    newTask,
    setNewTask,
    handleDragStart,
    handleDragOver,
    handleDrop,
    openAddTaskModal,
    handleAddTask,
    handleDeleteTask,
    handleTransferTask,
    fetchAllData,
    createColumn,
    reorderColumn
  };
}

export default useKanbanBoard;
